import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Phone, MessageCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { optimizedImage } from "@/lib/image";

const BG_IMAGE = "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/7htetnuv_photo%20boutique%20abd%20.jpeg";
const BROTHERS_PHOTO = "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/81pe4m47_basile%20papa%20photo%20siite%20.jpg";
const DANIEL_PHOTO = "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/9xcrnr7e_photo%20daniel%20site%20.jpeg";
const TRACTOR_PHOTO = "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/uv3eegvj_PHOTO-2026-05-25-19-40-07.jpg";
const ENCORE_LOGO = "https://customer-assets.emergentagent.com/job_maison-transition/artifacts/0lh85tu7_Capture%20d%E2%80%99e%CC%81cran%202026-05-16%20a%CC%80%2010.29.25.png";

const PhoneButton = ({ className, children }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className={className}>{children}</button>
    </DropdownMenuTrigger>
    <DropdownMenuContent className="bg-white">
      <DropdownMenuItem asChild>
        <a href="tel:0608691129" className="flex items-center gap-2 cursor-pointer">
          <Phone className="w-4 h-4" /> Appeler
        </a>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <a href="sms:0608691129" className="flex items-center gap-2 cursor-pointer">
          <MessageCircle className="w-4 h-4" /> Envoyer un SMS
        </a>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const HistoirePartenairesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Floating header */}
      <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
          <button
            onClick={() => navigate("/")}
            className="pointer-events-auto flex items-center gap-2 text-white bg-[#4A3728]/70 hover:bg-[#4A3728] backdrop-blur-sm px-4 py-2 rounded-full transition-colors"
            data-testid="btn-retour"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Retour</span>
          </button>

          <PhoneButton className="pointer-events-auto flex items-center gap-2 text-white bg-[#4A3728]/70 hover:bg-[#4A3728] backdrop-blur-sm px-4 py-2 rounded-full font-medium transition-colors">
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">06 08 69 11 29</span>
          </PhoneButton>
        </div>
      </div>

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img src={optimizedImage(BG_IMAGE, { w: 1920, q: 75 })} alt="Au Bon Débarras" className="w-full h-full object-cover" loading="eager" decoding="async" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#4A3728]/65 via-[#4A3728]/55 to-[#4A3728]/80" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-[#D4A373] mb-4 block">
              Notre Histoire
            </span>
            <h1 className="font-serif text-5xl md:text-7xl text-white mb-4">
              Une affaire de famille
            </h1>
            <p className="font-serif text-2xl text-[#D4A373]">Thomas & Basile</p>
          </motion.div>
        </div>
      </section>

      {/* Notre Histoire */}
      <section className="py-24 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            {/* Photo principale Basile & Thomas */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7 relative"
            >
              <img
                src={optimizedImage(BROTHERS_PHOTO, { w: 1100, q: 80 })}
                alt="Thomas et Basile — les deux frères"
                className="w-full h-[420px] md:h-[560px] object-cover rounded-lg shadow-2xl object-[center_30%] md:object-top"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#4A3728] text-white p-6 rounded-full transform -rotate-12 shadow-xl">
                <div className="text-center">
                  <div className="text-2xl font-serif">2ème</div>
                  <div className="text-xs uppercase tracking-wider">Génération</div>
                </div>
              </div>
              <p className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm text-[#4A3728] text-sm px-4 py-2 rounded-full font-medium">
                Thomas & Basile
              </p>
            </motion.div>

            {/* Texte */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="lg:col-span-5"
            >
              <div className="space-y-5">
                <p className="text-lg text-stone-700 leading-relaxed">
                  On peut dire qu'on est tombé dans la potion étant petits, dès que l'on pouvait, on accompagnait notre Papa et ses Compagnons dans leurs aventures débarrastesques&nbsp;!!!
                </p>
                <p className="text-lg text-stone-600 leading-relaxed">
                  Ils avaient bien entendu des surnoms&nbsp;: <strong className="text-[#4A3728]">Brisefer</strong>, <strong className="text-[#4A3728]">les Féroces</strong>, le plus célèbre et le plus proche de notre Famille était <strong className="text-[#4A3728]">Jean Santerre</strong>, un Homme comme on en fait plus, drôle et généreux, on sait qu'il veille sur nous&nbsp;!!!
                </p>
                <p className="text-lg text-stone-700 leading-relaxed italic">
                  On est fier de reprendre le flambeau, on le fait avec des souvenirs d'enfants, pourvu que ça dure...
                </p>
                <p className="text-lg text-stone-600 leading-relaxed">
                  Pas de jeunes entrepreneurs sans aînés, on a notre génie, il est connu comme le loup blanc, il s'appelle <strong className="text-[#4A3728]">Daniel Rouland</strong>, toujours présent quand il faut réfléchir avant d'agir...
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hommage à Daniel ROULAND — pleine largeur */}
      <section className="py-20 bg-[#FAFAF9]">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-10"
          >
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A3728]">
              Daniel ROULAND <span className="text-stone-500 font-light">dit</span> <span className="text-[#D97706]">«&nbsp;Le Génie&nbsp;»</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative max-w-4xl mx-auto"
          >
            <img
              src={optimizedImage(DANIEL_PHOTO, { w: 1280, q: 80 })}
              alt="Daniel ROULAND dit le génie"
              className="w-full h-[420px] md:h-[640px] object-cover rounded-xl shadow-2xl border-4 border-white object-[90%_10%]"
              loading="lazy"
              decoding="async"
            />
          </motion.div>
        </div>
      </section>

      {/* Hommage à Jean Santerre — pleine largeur */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-10"
          >
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A3728]">
              Jean DESAINT-DENIS <span className="text-stone-500 font-light">dit</span> <span className="text-[#D97706]">«&nbsp;Jean Santerre ou Bingo&nbsp;»</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative max-w-4xl mx-auto"
          >
            <img
              src={optimizedImage(TRACTOR_PHOTO, { w: 1280, q: 80 })}
              alt="Jean DESAINT-DENIS dit Jean Santerre ou Bingo"
              className="w-full h-[420px] md:h-[640px] object-cover rounded-xl shadow-2xl border-4 border-white object-[left_40%] md:object-center"
              loading="lazy"
              decoding="async"
            />
          </motion.div>
        </div>
      </section>

      {/* Partenaire */}
      <section className="py-24 bg-stone-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-sm font-medium tracking-widest uppercase text-[#D97706] mb-4 block">
              Notre Partenaire
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A3728]">
              Engagés ensemble
            </h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden"
          >
            <div className="grid md:grid-cols-5 gap-0">
              {/* Logo officiel Encore — fond sombre pour faire ressortir le logo vert */}
              <div className="md:col-span-2 bg-black flex items-center justify-center p-8 md:p-10 min-h-[280px]">
                <img
                  src={optimizedImage(ENCORE_LOGO, { w: 640, q: 85, output: "png" })}
                  alt="Encore — Environnement Cotentin Recyclage"
                  className="w-full max-w-[320px] h-auto object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Description */}
              <div className="md:col-span-3 p-10">
                <h3 className="font-serif text-2xl text-[#4A3728] mb-4">
                  Encore — Environnement Cotentin Recyclage
                </h3>
                <p className="text-stone-600 leading-relaxed mb-6">
                  Nous sommes en partenariat avec l'entreprise de recyclage <strong className="text-[#4A3728]">Encore — Environnement Cotentin Recyclage</strong>.
                  Grâce à eux, nous garantissons une seconde vie aux matières issues de nos débarras&nbsp;:
                  tri sélectif, revalorisation et recyclage responsable au cœur du Cotentin.
                </p>
                <a
                  href="https://www.encore-manche.fr/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#D97706] hover:bg-[#b96305] text-white rounded-full px-6 py-3 font-medium transition-all duration-300 hover:scale-105"
                  data-testid="partner-link-encore"
                >
                  Découvrir notre partenaire
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="py-8 text-center bg-[#FAFAF9]">
        <p className="text-stone-500 text-sm">
          Au Bon Débarras
        </p>
      </div>
    </div>
  );
};

export default HistoirePartenairesPage;
